<!-- components/form/SelectControl.vue -->
<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import FieldControl from './FieldControl.vue'
import InputShell from './InputShell.vue'

defineOptions({ inheritAttrs: false })

type ValidateOn = 'input' | 'blur' | 'submit'
type BaseItem = Record<string, unknown>
type TitleProp = keyof BaseItem | ((i: BaseItem) => string)
type ValueProp = keyof BaseItem | ((i: BaseItem) => unknown)

const props = withDefaults(
  defineProps<{
    /** Vee-Validate field name (obrigatório) */
    name: string

    /** Rótulo visual (flutua ao focar/selecionar) */
    label?: string
    required?: boolean
    hint?: string

    /** v-model externo (opcional) */
    modelValue?: unknown

    /** Estratégia de validação */
    validateOn?: ValidateOn

    /** Fonte de dados */
    items?: ReadonlyArray<BaseItem>
    fetchItems?: (() => Promise<ReadonlyArray<BaseItem>>) | undefined
    autoLoad?: boolean
    loadOnFocus?: boolean

    /** Mapeamento de título/valor */
    itemTitle?: TitleProp
    itemValue?: ValueProp
    returnObject?: boolean

    /** Comportamento do select */
    multiple?: boolean
    chips?: boolean
    clearable?: boolean

    /** Aparência/estado */
    forId?: string
    disabled?: boolean
    readonly?: boolean
    density?: 'default' | 'comfortable' | 'compact'

    /** Regras locais do vee-validate (opcional) */
    rules?: unknown

    /** Área de detalhes (hint/erros/contador). true = reserva altura sempre */
    reserveDetails?: boolean
    /** Controle bruto do Vuetify; quando reserveDetails=true, é ignorado e vira false */
    hideDetails?: boolean | 'auto'

    /**
     * Faz a label "subir" automaticamente quando o valor vier do código
     * (útil em formulários de edição/carregados do backend).
     */
    autoFloatOnProgrammaticValue?: boolean
  }>(),
  {
    label: '',
    required: false,
    hint: '',
    modelValue: undefined,
    validateOn: undefined,

    items: () => [],
    fetchItems: undefined,
    autoLoad: true,
    loadOnFocus: false,

    itemTitle: (i: BaseItem) => String(i['label'] ?? ''),
    itemValue: (i: BaseItem) => i['value'],

    returnObject: false,
    multiple: false,
    chips: false,
    clearable: false,

    forId: undefined,
    disabled: false,
    readonly: false,
    density: 'default',

    rules: undefined,

    reserveDetails: true,
    hideDetails: 'auto',

    autoFloatOnProgrammaticValue: true,
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', v: unknown): void
  (e: 'focus'): void
  (e: 'blur'): void
  (e: 'enter'): void
  (e: 'clear'): void
  (e: 'loaded', items: ReadonlyArray<BaseItem>): void
}>()

/** i18n global + helper p/ traduzir chaves de erro (ex.: "validation.name") */
const { t } = useI18n({ useScope: 'global' })
function translateError(e: unknown): string | string[] | null {
  if (!e) return null
  if (Array.isArray(e)) {
    const out = e
      .map((x) => (typeof x === 'string' ? t(x) : String(x)))
      .filter(Boolean)
    return out.length ? out : null
  }
  return typeof e === 'string' ? t(e) : String(e)
}

/** estado interno */
const options = ref<ReadonlyArray<BaseItem>>(props.items)
const loading = ref<boolean>(false)
const loadedOnce = ref<boolean>(false)
const isFocused = ref<boolean>(false)
/** flutua label só depois de interação do usuário (ou programática, se habilitado) */
const wasInteracted = ref<boolean>(false)

/** helpers */
const asTitleFn = (tprop: TitleProp) =>
  typeof tprop === 'function' ? tprop : (i: BaseItem) => String(i[tprop] ?? '')

const asValueFn = (vprop: ValueProp) =>
  typeof vprop === 'function' ? vprop : (i: BaseItem) => i[vprop]

function hasSelection(val: unknown, multiple: boolean): boolean {
  if (multiple) return Array.isArray(val) && val.length > 0
  if (val === null || val === undefined) return false
  if (typeof val === 'object') return true
  return String(val).length > 0
}

/** Decide hide-details (reserva altura quando reserveDetails=true) */
const resolvedHideDetails = computed<boolean | 'auto'>(() => {
  if (props.reserveDetails) return false
  if (props.hideDetails !== 'auto') return props.hideDetails
  return 'auto'
})

/** carregar items */
const loadItems = async () => {
  if (!props.fetchItems || loading.value) return
  loading.value = true
  try {
    const data = await props.fetchItems()
    options.value = Array.isArray(data) ? data : []
    loadedOnce.value = true
    emit('loaded', options.value)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  if (props.items.length) options.value = props.items
  if (props.fetchItems && props.autoLoad && !loadedOnce.value) {
    await loadItems()
  }
})

/** foco/blur/menu */
function onControlFocus() {
  isFocused.value = true
  wasInteracted.value = true
  emit('focus')
}
function onControlBlur() {
  isFocused.value = false
  emit('blur')
}
async function onMenuOpen() {
  if (props.fetchItems && props.loadOnFocus && !loadedOnce.value) {
    await loadItems()
  }
}

/** handlers pra evitar .value no template */
function onUpdateModelValue(v: unknown) {
  wasInteracted.value = true
  emit('update:modelValue', v)
}
function onClearClick() {
  wasInteracted.value = true
  emit('clear')
}

/** Sobe label quando valor é definido programaticamente (edição) */
if (props.autoFloatOnProgrammaticValue) {
  watch(
    () => props.modelValue,
    (newVal) => {
      const hasVal =
        newVal !== undefined &&
        newVal !== null &&
        (Array.isArray(newVal) ? newVal.length > 0 : String(newVal).length > 0)
      if (hasVal) wasInteracted.value = true
    },
    { immediate: true }
  )
}
</script>

<template>
  <FieldControl
    :name="props.name"
    :model-value="props.modelValue"
    :validate-on="props.validateOn"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :rules="props.rules"
    @update:modelValue="onUpdateModelValue"
    @focus="onControlFocus"
    @blur="onControlBlur"
    @enter="emit('enter')"
  >
    <template #default="{ bind, error }">
      <!-- label externa só se NÃO focado, NÃO erro e (NÃO interagiu OU NÃO tem seleção) -->
      <InputShell
        :label="
          isFocused ||
          !!error ||
          (wasInteracted && hasSelection(bind.modelValue, !!props.multiple))
            ? ''
            : props.label
        "
        :required="props.required"
        :hint="props.hint"
        :error="translateError(error)"
        :for-id="props.forId"
        :disabled="props.disabled"
        :readonly="props.readonly"
      >
        <template #control>
          <v-select
            v-bind="{ ...bind, ...$attrs }"
            :id="props.forId"
            :items="options"
            :item-title="asTitleFn(props.itemTitle)"
            :item-value="asValueFn(props.itemValue)"
            :return-object="props.returnObject"
            :multiple="props.multiple"
            :chips="props.chips"
            :clearable="props.clearable"
            :density="props.density"
            variant="outlined"
            :loading="loading"
            :error="!!error"
            :error-messages="[]"
            :messages="[]"
            :hide-details="resolvedHideDetails"
            :label="
              isFocused ||
              !!error ||
              (wasInteracted && hasSelection(bind.modelValue, !!props.multiple))
                ? props.label
                : ''
            "
            @focus="onControlFocus"
            @blur="onControlBlur"
            @update:menu="(v: boolean) => v && onMenuOpen()"
            @click:clear="onClearClick"
          >
            <template v-if="$slots.selection" #selection="slotProps">
              <slot name="selection" v-bind="slotProps" />
            </template>
            <template v-if="$slots.chip" #chip="slotProps">
              <slot name="chip" v-bind="slotProps" />
            </template>
            <template v-if="$slots.item" #item="slotProps">
              <slot name="item" v-bind="slotProps" />
            </template>
            <template v-if="$slots['prepend-item']" #prepend-item>
              <slot name="prepend-item" />
            </template>
            <template v-if="$slots['append-item']" #append-item>
              <slot name="append-item" />
            </template>
            <template v-if="$slots['no-data']" #no-data>
              <slot name="no-data" />
            </template>
          </v-select>
        </template>

        <template v-if="$slots['label-append']" #label-append>
          <slot name="label-append" />
        </template>
        <template v-if="$slots.hint" #hint>
          <slot name="hint" />
        </template>
        <template v-if="$slots.actions" #actions>
          <slot name="actions" />
        </template>
      </InputShell>
    </template>
  </FieldControl>
</template>
